(() => {
  'use strict';

  if (window.__tjcDiscountEngineDisplayInitialized) return;
  window.__tjcDiscountEngineDisplayInitialized = true;

  const DISPLAY = '[data-discount-engine-display]';
  const APP_HOST = '[data-shoplc-discount-engine]';
  const VALUES = '[data-discount-engine-values]';
  const simulatorScenarios = new Set([
    'code',
    'fixed',
    'automatic',
    'shipping',
    'none',
    'error',
    'mixed',
  ]);
  const simulatorParameter = new URLSearchParams(window.location.search).get('discount_engine_sim');
  let simulatorScenario = simulatorParameter;
  if (!simulatorScenario) {
    try {
      simulatorScenario = window.sessionStorage.getItem('tjc:discount-engine-sim');
    } catch (error) {
      console.error('[discount-engine-display] Could not read simulator settings.', error);
    }
  }
  const copyFeedbackTimers = new WeakMap();
  const renderedStates = new WeakMap();
  const MESSAGE_TIMEOUT_MS = 3000;
  const messageTokens = new WeakMap();
  const messageRequests = new Map();
  const appMessages = new Map();
  const plpMessageBatches = new Map();

  function numericId(value) {
    return String(window.CalloutApi?.toNumericResourceId?.(value) ?? value ?? '');
  }

  function messageKey(page, productId, variantId) {
    return `${page}:${numericId(productId)}:${numericId(variantId)}`;
  }

  function captureAppMessages(api) {
    if (!api?.fetchCallouts || api.fetchCallouts.capturesDiscountMessages) return;
    const fetchCallouts = api.fetchCallouts;
    const fetchAndCapture = function (page, extra = {}) {
      const request = fetchCallouts.call(this, page, extra);
      if (page === 'pdp' && extra.productId) {
        appMessages.set(
          messageKey(page, extra.productId, extra.variantId),
          request.then((result) => (result?.data ?? result)?.message || '').catch(() => ''),
        );
      } else if (Array.isArray(extra.productIds)) {
        extra.productIds.forEach((productId) => {
          appMessages.set(
            messageKey(page, productId, ''),
            request.then((result) => result?.data?.[productId]?.message || '').catch(() => ''),
          );
        });
      }
      return request;
    };
    fetchAndCapture.capturesDiscountMessages = true;
    api.fetchCallouts = fetchAndCapture;
  }

  // Must wrap fetchCallouts before the app's values script sends its first request, and that script runs as
  // soon as CalloutApi exists, so wrap it at the moment the app assigns it.
  function captureWhenCalloutApiLoads() {
    if (window.CalloutApi) {
      captureAppMessages(window.CalloutApi);
      return;
    }
    let calloutApi;
    try {
      Object.defineProperty(window, 'CalloutApi', {
        configurable: true,
        enumerable: true,
        get: () => calloutApi,
        set: (api) => {
          calloutApi = api;
          captureAppMessages(api);
        },
      });
    } catch (error) {
      console.error('[discount-engine-display] Could not watch for the callout API.', error);
    }
  }

  function whenCalloutApi() {
    return new Promise((resolve) => {
      if (window.CalloutApi) resolve(window.CalloutApi);
      else if (window.whenCalloutApiReady) window.whenCalloutApiReady(resolve);
      else resolve(null);
    });
  }

  function queuePlpMessage(page, productId) {
    if (!plpMessageBatches.has(page)) {
      const ids = new Set();
      const promise = new Promise((resolve) => window.setTimeout(resolve, 0)).then(async () => {
        plpMessageBatches.delete(page);
        const api = await whenCalloutApi();
        if (!api) return {};
        const result = await api.fetchPlp([...ids], page);
        return result?.data || {};
      });
      plpMessageBatches.set(page, { ids, promise });
    }
    const batch = plpMessageBatches.get(page);
    batch.ids.add(productId);
    return batch.promise.then((data) => data[productId]?.message || '');
  }

  function fetchMessage(display) {
    const productId = display.dataset.productId;
    const surface = display.dataset.surface;
    const page = surface === 'pdp' || surface === 'cart' ? surface : 'plp';
    const host =
      page === 'pdp'
        ? document.querySelector(`${APP_HOST}[data-surface="pdp"][data-product-id="${productId}"]`)
        : null;
    const variantId = page === 'pdp' ? host?.dataset.variantId || display.dataset.variantId || '' : '';
    const key = messageKey(page, productId, variantId);
    const appMessage = appMessages.get(key);
    if (appMessage) return appMessage;
    if (!messageRequests.has(key)) {
      const request = (
        page === 'pdp'
          ? whenCalloutApi().then(async (api) => {
              if (!api) return '';
              const result = await api.fetchPdp(productId, variantId);
              return result?.data?.message || '';
            })
          : queuePlpMessage(page, productId)
      ).catch((error) => {
        messageRequests.delete(key);
        console.error('[discount-engine-display] Could not load the discount message.', error);
        return '';
      });
      messageRequests.set(key, request);
    }
    return messageRequests.get(key);
  }

  function messageWithTimeout(display) {
    return Promise.race([
      fetchMessage(display),
      new Promise((resolve) => window.setTimeout(() => resolve(''), MESSAGE_TIMEOUT_MS)),
    ]);
  }

  function hasVisibleContent(html) {
    const template = document.createElement('template');
    template.innerHTML = String(html || '');
    return Boolean(template.content.textContent.trim() || template.content.querySelector('img, svg'));
  }

  function matchingDisplays(productId, surface, source) {
    const scope =
      source?.closest?.(
        '.product-card, product-card, [data-product-card], product-info, [id^="ProductInformation-"]',
      ) || source?.closest?.('.shopify-section, [data-section-id]');
    const displays = scope?.querySelectorAll(DISPLAY) || document.querySelectorAll(DISPLAY);
    return [...displays].filter(
      (display) =>
        display.dataset.productId === String(productId) &&
        display.dataset.surface === surface,
    );
  }

  function formattedAmount(values) {
    const amount = Number(values.discountValue);
    if (!Number.isFinite(amount) || amount <= 0) return '';
    const text = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
    return values.discountValueType === 'percentage' ? `${text}% OFF` : `£${text} OFF`;
  }

  function offerText(values, lead) {
    if (values.discountType === 'free_shipping') return 'Free Delivery';
    const amount = formattedAmount(values);
    return amount ? `${lead ? `${lead} ` : ''}${amount}` : String(values.title || '');
  }

  function renderOffer(offer, text, lead) {
    const leadWithSpace = lead ? `${lead} ` : '';
    const leadElement = document.createElement('span');
    leadElement.className = 'coupon-text__lead';
    const discountElement = document.createElement('span');
    discountElement.className = 'coupon-text__discount';

    if (leadWithSpace && text.startsWith(leadWithSpace)) {
      leadElement.textContent = leadWithSpace;
      discountElement.textContent = text.slice(leadWithSpace.length);
      offer.replaceChildren(leadElement, discountElement);
      return;
    }

    discountElement.textContent = text;
    offer.replaceChildren(discountElement);
  }

  function render(display, values) {
    const offer = display.querySelector('[data-discount-engine-offer]');
    const codeRow = display.querySelector('[data-discount-engine-code-row]');
    const codeElement = display.querySelector('[data-discount-engine-code]');
    const copyButton = display.querySelector('[data-discount-engine-copy]');
    const status = display.querySelector('[data-discount-engine-status]');
    const lead = (display.dataset.offerLead ?? 'Get').trim();
    const hasValues = values && typeof values === 'object';
    const code = values && values.discountCode ? String(values.discountCode).trim() : '';
    const text = hasValues ? offerText(values, lead).trim() : '';
    const discountType = values?.discountType ? String(values.discountType) : '';
    const signature = JSON.stringify([text, code, lead, discountType]);
    const previous = renderedStates.get(display);
    if (
      previous?.signature === signature &&
      previous.offer === offer && previous.codeRow === codeRow &&
      previous.codeElement === codeElement && previous.copyButton === copyButton &&
      previous.status === status
    ) return;
    renderedStates.set(display, { signature, offer, codeRow, codeElement, copyButton, status });
    const token = {};
    messageTokens.set(display, token);
    if (!hasValues || !display.hasAttribute('data-use-app-message')) {
      paint(display, values, '', { offer, codeRow, codeElement, copyButton, status, code, text, lead });
      return;
    }
    void messageWithTimeout(display).then((message) => {
      if (!display.isConnected || messageTokens.get(display) !== token) return;
      const html = hasVisibleContent(message) ? message : '';
      paint(display, values, html, { offer, codeRow, codeElement, copyButton, status, code, text, lead });
    });
  }

  function paint(display, values, message, { offer, codeRow, codeElement, copyButton, status, code, text, lead }) {
    display.toggleAttribute('data-discount-engine-message', Boolean(message));
    if (copyButton) {
      clearCopyFeedback(copyButton);
      copyButton.dataset.discountCode = code;
    }
    display.toggleAttribute('data-discount-engine-no-code', !code);
    if (values && values.discountType) display.dataset.discountType = String(values.discountType);
    else display.removeAttribute('data-discount-type');

    display.closest('.discount-engine-ui-stack')?.classList.remove('discount-engine-ui-stack--pending');

    if (!message && !text && !code) {
      display.hidden = true;
      if (offer) offer.replaceChildren();
      if (codeElement) codeElement.textContent = '';
      if (codeRow) codeRow.hidden = true;
      if (copyButton) copyButton.hidden = true;
      if (status) status.textContent = '';
      return;
    }

    if (offer && message) {
      if (window.CalloutApi?.renderCalloutMessage) window.CalloutApi.renderCalloutMessage(offer, message);
      else offer.innerHTML = message;
    } else if (offer) {
      renderOffer(offer, text, lead);
    }
    if (codeElement) codeElement.textContent = code;
    if (codeRow) codeRow.hidden = false;
    if (copyButton) {
      copyButton.hidden = !code;
      copyButton.dataset.discountCode = code;
    }
    if (status) status.textContent = '';
    display.hidden = false;
  }

  function valuesFromHost(host) {
    try {
      const json = host.querySelector(VALUES)?.textContent;
      return json ? JSON.parse(json) : null;
    } catch (error) {
      console.error('[discount-engine-display] Could not parse discount values.', error);
      return null;
    }
  }

  function onValues(event) {
    const source = event.target;
    if (!(source instanceof Element)) return;

    if (source.matches(DISPLAY)) {
      render(source, event.detail);
      return;
    }

    if (!source.matches(APP_HOST)) return;
    matchingDisplays(source.dataset.productId, source.dataset.surface, source).forEach((display) =>
      render(display, event.detail),
    );
  }

  function hydrateFromExistingHosts() {
    const appHosts = document.querySelectorAll(APP_HOST);
    const displays = document.querySelectorAll(DISPLAY);
    const hostsByKey = new Set();
    appHosts.forEach((host) => {
      hostsByKey.add(`${host.dataset.surface}:${host.dataset.productId}`);
    });
    appHosts.forEach((host) => {
      const values = valuesFromHost(host);
      if (values === null) return;
      matchingDisplays(host.dataset.productId, host.dataset.surface, host).forEach((display) =>
        render(display, values),
      );
    });

    if (simulatorScenario && simulatorScenarios.has(simulatorScenario)) return;
    displays.forEach((display) => {
      if (hostsByKey.has(`${display.dataset.surface}:${display.dataset.productId}`)) return;
      display.closest('.discount-engine-ui-stack')?.classList.remove('discount-engine-ui-stack--pending');
    });
  }

  function clearCopyFeedback(button) {
    const timer = copyFeedbackTimers.get(button);
    if (timer !== undefined) window.clearTimeout(timer);
    copyFeedbackTimers.delete(button);
    const label = button.querySelector('[data-discount-engine-copy-label]');
    if (label) label.textContent = label.dataset.defaultLabel || 'Copy Code';
    delete button.dataset.copied;
  }

  function showCopyFeedback(button) {
    const existingTimer = copyFeedbackTimers.get(button);
    if (existingTimer) window.clearTimeout(existingTimer);
    const label = button.querySelector('[data-discount-engine-copy-label]');
    if (label) label.textContent = 'Code Copied';
    button.dataset.copied = 'true';
    const timer = window.setTimeout(() => {
      if (button.isConnected) {
        if (label) label.textContent = label.dataset.defaultLabel || 'Copy Code';
        delete button.dataset.copied;
      }
      copyFeedbackTimers.delete(button);
    }, 5000);
    copyFeedbackTimers.set(button, timer);
  }

  async function copyCode(button) {
    const code = button.dataset.discountCode;
    const status = button.closest(DISPLAY)?.querySelector('[data-discount-engine-status]');
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      if (!button.isConnected || button.dataset.discountCode !== code) return;
      if (status) status.textContent = 'Code copied';
      showCopyFeedback(button);
    } catch (error) {
      console.error('[discount-engine-display] Could not copy the discount code.', error);
      if (status) status.textContent = 'Unable to copy code';
    }
  }

  captureWhenCalloutApiLoads();
  document.addEventListener('shoplc:discount-engine-values', onValues);
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-discount-engine-copy]');
    if (button) void copyCode(button);
  });
  document.addEventListener('shopify:section:load', hydrateFromExistingHosts);
  hydrateFromExistingHosts();

  if (simulatorScenario) {
    const simulatorUrl = document.querySelector(DISPLAY)?.dataset.simulatorSrc;
    if (!simulatorUrl) {
      console.error('[discount-engine-display] Simulation requested, but no simulator asset URL is available.');
    } else {
      import(simulatorUrl).catch((error) => {
        console.error('[discount-engine-display] Could not load the discount simulator.', error);
      });
    }
  }
})();
